import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Curated disposable / temporary inbox domains for signup abuse prevention (SUSEE-1534).
 * Keep this list maintainable — not every throwaway provider on the internet.
 */
export const DISPOSABLE_EMAIL_DOMAINS: readonly string[] = [
  // Yopmail family
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'cool.fr.nf',
  'jetable.fr.nf',
  'courriel.fr.nf',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
  'monmail.fr.nf',
  // Mailinator
  'mailinator.com',
  'mailinator.net',
  'mailinator2.com',
  'mailinater.com',
  // Guerrilla Mail
  'guerrillamail.com',
  'guerrillamail.org',
  'guerrillamail.net',
  'guerrillamail.biz',
  'guerrillamail.de',
  'sharklasers.com',
  'grr.la',
  'guerrillamailblock.com',
  // Temp-Mail / related
  'temp-mail.org',
  'tempmail.com',
  'tempmailo.com',
  'tempmailaddress.com',
  // 10 Minute Mail
  '10minutemail.com',
  '10minutemail.net',
  '10minmail.com',
  // Trash / fake
  'trashmail.com',
  'trashmail.me',
  'trashmail.net',
  'fakeinbox.com',
  'mailnesia.com',
  'maildrop.cc',
  'discard.email',
  'discardmail.com',
  'throwawaymail.com',
  'getnada.com',
  'emailondeck.com',
  'moakt.com',
  'tmpmail.org',
  'tmpmail.net',
];

const DISPOSABLE_DOMAIN_SET = new Set(
  DISPOSABLE_EMAIL_DOMAINS.map((domain) => domain.toLowerCase())
);

/** Extract lowercase domain from an email, or null if the address has no @ host. */
export function extractEmailDomain(email: string | null | undefined): string | null {
  const raw = String(email ?? '').trim().toLowerCase();
  const at = raw.lastIndexOf('@');
  if (at < 0 || at === raw.length - 1) {
    return null;
  }
  return raw.slice(at + 1);
}

/**
 * True when the email's domain (or a parent of it) is on the disposable denylist.
 * e.g. `foo.yopmail.com` matches `yopmail.com`.
 */
export function isDisposableEmailDomain(email: string | null | undefined): boolean {
  const domain = extractEmailDomain(email);
  if (!domain) {
    return false;
  }
  if (DISPOSABLE_DOMAIN_SET.has(domain)) {
    return true;
  }
  for (const blocked of DISPOSABLE_DOMAIN_SET) {
    if (domain.endsWith(`.${blocked}`)) {
      return true;
    }
  }
  return false;
}

/** Angular reactive-forms validator — error key: `disposableEmail`. */
export function disposableEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || String(value).trim() === '') {
      return null;
    }
    return isDisposableEmailDomain(String(value)) ? { disposableEmail: true } : null;
  };
}
